import { useMutation } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { DetectBlur } from '@/containers/util-containers'
import type {
  ChecklistEntries,
  ChecklistsType,
  ChecklistType,
} from '@/modules/checklists/types'
import type {
  AllChecklistsQuery,
  BatchChangeChecklistItemsMutation,
  BatchChangeChecklistItemsMutationVariables,
  PatchChecklistItem,
} from '@/modules/checklists/types.graphql'
import React, { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import styled from 'styled-components'
import { useBreadcrumbs, useOnErrorAuto, useUser } from '@/utils/hooks'
import { ChecklistCard } from '../components/ChecklistCard'
import { useUserChecklists } from '../hooks'
import { BATCH_CHANGE_CHECKLIST_ITEMS } from '../mutations'
import { transformChecklistQueryData } from '../util'

const Wrapper = styled.div`
  ${props => props.theme.defaultContentWrapper};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const TodaysMandatoryChecklists: React.FC = () => {
  const translations = useTranslate({
    mandatoryChecklists: 'common.mandatory-checklists',
    loading: 'common.loading...',
  })
  const onErrorAuto = useOnErrorAuto()
  const { setOverride, setOverrides, overrides } = useBreadcrumbs()

  const [dirtyChecklistItems, setDirtyChecklistItems] = useState<
    PatchChecklistItem[]
  >([])

  const [batchPatchMutation] = useMutation<
    BatchChangeChecklistItemsMutation,
    BatchChangeChecklistItemsMutationVariables
  >(BATCH_CHANGE_CHECKLIST_ITEMS, {
    refetchQueries: ['AllChecklists'],
    onCompleted() {
      setDirtyChecklistItems([])
    },
    onError: onErrorAuto(),
  })
  const user = useUser()

  const handleClickOutside = useCallback(() => {
    if (dirtyChecklistItems.length > 0) {
      void batchPatchMutation({
        variables: {
          updatedChecklistItems: dirtyChecklistItems,
        },
      })
    }
  }, [dirtyChecklistItems, batchPatchMutation])

  async function handleSubmit(checklist: ChecklistType | null) {
    if (dirtyChecklistItems.length > 0) {
      await batchPatchMutation({
        variables: {
          updatedChecklistItems: dirtyChecklistItems,
        },
      })
    }
    if (!checklist) return
    updateForm({
      [checklist.id]: null,
    })
  }

  const { formValues, updateForm } = useForm<ChecklistsType>({
    values: {},
  })

  const handleChecklistsLoaded = (data: AllChecklistsQuery) => {
    const queryData = transformChecklistQueryData(data)
    updateForm(queryData)
  }

  const { userChecklists: data, loading } = useUserChecklists(
    user,
    handleChecklistsLoaded,
    true
  )

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      if (dirtyChecklistItems.length > 0) {
        void batchPatchMutation({
          variables: {
            updatedChecklistItems: dirtyChecklistItems,
          },
        })
      }
    })
    return () => {
      window.removeEventListener('beforeunload', () => {
        if (dirtyChecklistItems.length > 0) {
          void batchPatchMutation({
            variables: {
              updatedChecklistItems: dirtyChecklistItems,
            },
          })
        }
      })
    }
  }, [batchPatchMutation, dirtyChecklistItems])

  function onChange(
    checklist: ChecklistType,
    checklistItem: PatchChecklistItem,
    newValue: ChecklistEntries
  ) {
    updateForm({
      [checklist.id]: {
        id: checklist.id,
        title: checklist.title,
        entries: newValue,
        active: checklist.active,
      },
    })
    if (
      dirtyChecklistItems.filter(dirtyItem => dirtyItem.id === checklistItem.id)
        .length === 0
    ) {
      setDirtyChecklistItems(dirtyChecklistItems.concat([checklistItem]))
    }
  }

  useEffect(() => {
    if (
      !data &&
      loading &&
      overrides['mandatory-checklists'] !== translations.loading
    )
      setOverride('mandatory-checklists', translations.loading)
    else if (
      data &&
      !loading &&
      overrides['mandatory-checklists'] === translations.loading
    ) {
      setOverride('mandatory-checklists', translations.mandatoryChecklists)
    }
  }, [
    setOverride,
    setOverrides,
    translations.loading,
    overrides,
    data,
    loading,
    translations.mandatoryChecklists,
  ])

  const showChecklists =
    data?.allChecklists &&
    data.allChecklists.edges.filter(
      edge => edge.node.active && edge.node.mandatory
    ).length > 0

  if (!loading && !showChecklists) return <Navigate to="/dashboard" />

  return (
    <DetectBlur
      disableTouchEnd
      active={dirtyChecklistItems.length > 0}
      onClickOutside={() => handleClickOutside()}
    >
      <Wrapper>
        <h1 className="text-4xl font-bold">
          {translations.mandatoryChecklists}
        </h1>
        {showChecklists &&
          !loading &&
          data &&
          Object.entries(formValues).map(([checklistId, checklist]) => (
            <ChecklistCard
              key={checklistId}
              checklist={checklist}
              onChange={onChange}
              onSubmit={() => handleSubmit(checklist)}
              removeChecklistFromForm={() => {
                updateForm({
                  [checklistId]: null,
                })
              }}
              mandatory={true}
            />
          ))}
      </Wrapper>
    </DetectBlur>
  )
}
